import React, { useCallback } from 'react';
import useImportCapitalNeedFileMutation from '../../hooks/useImportCapitalNeedFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import type { ObjectImportConfig } from '../asset/SubmitModal';
import SubmitConfirmModal from '../modals/SubmitConfirmModal';
import { buildAssetLookupImportConfig, buildTenantImportConfig } from '../base/commonImportConfigs';

interface CapitalNeedSubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

const CapitalNeedSubmitModal: React.FC<CapitalNeedSubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { importCapitalNeedFile } = useImportCapitalNeedFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    const tenantImportConfig = buildTenantImportConfig(selectedTenant);
    const assetImportConfig = buildAssetLookupImportConfig(uniqueField, mapping);

    const capitalNeedImportConfig: ObjectImportConfig = {
      model: 'CapitalNeedModel',
      thirdPartyId: 'thirdPartyId',
      thirdPartyIdSource: 'thirdPartySource',
      columnMap: {
        thirdPartyId: mapping['third_party_id'],
        thirdPartySource: { defaultValue: 'csv' },
        // Imported Capital Needs always default to "Planned" status; not user-mappable.
        status: { defaultValue: 'planned' },
        title: mapping['title'],
        type: mapping['type'],
        cost: mapping['cost'],
        plannedDate: mapping['planned_date'],
        // Optional fields
        description: isColumnMapped('description') ? mapping['description'] : undefined,
        // Related entities
        tenantId: {
          relatedTo: 'TenantModel',
        },
        assetId: {
          relatedTo: 'AssetModel',
        },
      },
      updateColumns: ['title', 'type', 'cost', 'plannedDate', 'description', 'assetId'],
      uniqueForTenant: true,
    };

    const config: ObjectImportConfig[] = [
      tenantImportConfig,
      assetImportConfig,
      capitalNeedImportConfig,
    ];

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  return (
    <SubmitConfirmModal
      visible={visible}
      uploadedFile={uploadedFile}
      selectedTenant={selectedTenant}
      entityLabelPlural="Capital Needs"
      entityLabelSingularLower="capital need"
      generateImportConfig={generateImportConfig}
      importFile={importCapitalNeedFile}
      onHiding={onHiding}
      onImportStart={onImportStart}
      onSuccess={onSuccess}
      onFailed={onFailed}
    />
  );
};

export default CapitalNeedSubmitModal;
